import { c as _c } from "react/compiler-runtime";
import React, { useState, useSyncExternalStore } from "react";
import Head from "next/head";
import Script from "next/script";
import { Formsparkform } from "../../../../studio/sanity.types";
import styles from "./FormSparkForm.module.scss";
import { EffektButton, EffektButtonVariant } from "../../../shared/components/EffektButton/EffektButton";
import { RadioButton } from "../../../shared/components/RadioButton/RadioButton";
import { EffektCheckbox } from "../../../shared/components/EffektCheckbox/EffektCheckbox";
import { redirectUrlFor, wasSubmitted } from "./formsparkRedirect";

// Type definitions for form state
type FormState = Record<string, string | boolean>;

// Type guard to check if a field has options
const hasOptions = (field: any): field is {
  options: Array<{
    value: string;
    label: string;
  }>;
} => {
  return field.options && Array.isArray(field.options);
};

// Initial state for the controlled inputs
const getInitialFormState = (fields: Formsparkform["fields"]): FormState => {
  const initialState: FormState = {};
  fields?.forEach(field => {
    if (field.field_type === "checkbox") {
      initialState[field.field_name || ""] = false;
    } else {
      initialState[field.field_name || ""] = "";
    }
  });
  return initialState;
};

// There is no event for URL changes to subscribe to; the URL is read again on every render
const subscribeToNothing = () => () => {};
const getPageUrl = () => window.location.href;
const getServerPageUrl = () => null;
export const FormsparkForm: React.FC<{
  formData: Formsparkform;
}> = t0 => {
  const $ = _c(31);
  const {
    formData
  } = t0;
  let t1;
  if ($[0] !== formData.fields) {
    t1 = () => getInitialFormState(formData.fields);
    $[0] = formData.fields;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [formState, setFormState] = useState(t1);
  const [prevFields, setPrevFields] = useState(formData.fields);
  if (formData.fields !== prevFields) {
    setPrevFields(formData.fields);
    setFormState(getInitialFormState(formData.fields));
  }
  const pageUrl = useSyncExternalStore(subscribeToNothing, getPageUrl, getServerPageUrl);
  const formId = formData.form_id;
  let t2;
  if ($[2] !== formId || $[3] !== pageUrl) {
    t2 = !!pageUrl && !!formId && wasSubmitted(pageUrl, formId);
    $[2] = formId;
    $[3] = pageUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const submitted = t2;
  let t3;
  if ($[5] !== formId || $[6] !== pageUrl) {
    t3 = pageUrl && formId ? redirectUrlFor(pageUrl, formId) : undefined;
    $[5] = formId;
    $[6] = pageUrl;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const redirectUrl = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (fieldName, value) => {
      setFormState(prev => ({
        ...prev,
        [fieldName]: value
      }));
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleInputChange = t4;
  let t5;
  if ($[9] !== formState) {
    t5 = field => {
      const fieldName_0 = field.field_name || "";
      const validation = field.validation || {};
      const commonProps = {
        name: fieldName_0,
        id: fieldName_0,
        required: validation.required
      };
      if (validation.min_length) {
        commonProps.minLength = validation.min_length;
      }
      if (validation.max_length) {
        commonProps.maxLength = validation.max_length;
      }
      if (validation.pattern) {
        commonProps.pattern = validation.pattern;
        if (validation.pattern_message) {
          commonProps.title = validation.pattern_message;
        }
      }
      if (field.autocomplete) {
        commonProps.autoComplete = field.autocomplete;
      }
      switch (field.field_type) {
        case "textarea":
          {
            return <textarea {...commonProps} value={formState[fieldName_0] as string || ""} onChange={e_2 => handleInputChange(fieldName_0, e_2.target.value)} placeholder={field.placeholder} rows={field.rows || 4} />;
          }
        case "select":
          {
            return <select {...commonProps} value={formState[fieldName_0] as string || ""} onChange={e_1 => handleInputChange(fieldName_0, e_1.target.value)}><option value="">Choose an option</option>{hasOptions(field) && field.options.map(_temp)}</select>;
          }
        case "radio":
          {
            return <div role="radiogroup" aria-labelledby={`${fieldName_0}-label`} className={styles.radioGroup}>{hasOptions(field) && field.options.map((option, index) => <div key={index}><RadioButton name={fieldName_0} selected={formState[fieldName_0] === option.value} onSelect={() => handleInputChange(fieldName_0, option.value)} title={option.label} data_cy={`${fieldName_0}-${index}`} required={index === 0 && (formState[fieldName_0] === "" || formState[fieldName_0] === undefined)} /></div>)}</div>;
          }
        case "checkbox":
          {
            return <div><EffektCheckbox required={commonProps.required} checked={!!formState[fieldName_0]} onChange={checked => handleInputChange(fieldName_0, checked)}>{field.field_label}</EffektCheckbox></div>;
          }
        case "number":
          {
            const numberProps = {
              ...commonProps
            };
            if (validation.min_value !== undefined) {
              numberProps.min = validation.min_value;
            }
            if (validation.max_value !== undefined) {
              numberProps.max = validation.max_value;
            }
            return <input {...numberProps} type="number" value={formState[fieldName_0] as string || ""} onChange={e_0 => handleInputChange(fieldName_0, e_0.target.value)} placeholder={field.placeholder} />;
          }
        default:
          {
            return <input {...commonProps} type={field.field_type || "text"} value={formState[fieldName_0] as string || ""} onChange={e => handleInputChange(fieldName_0, e.target.value)} placeholder={field.placeholder} />;
          }
      }
    };
    $[9] = formState;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const renderField = t5;
  if (!formData || !formData.fields) {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div>No form data provided</div>;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t6;
  if ($[12] !== formData.turnstile_site_key) {
    t6 = formData.turnstile_site_key && <><Head><link rel="preconnect" href="https://challenges.cloudflare.com" /></Head><Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="afterInteractive" /></>;
    $[12] = formData.turnstile_site_key;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = `form-${formData.form_id}`;
  let t8;
  if ($[14] !== formData.fields || $[15] !== formData.form_id || $[16] !== formData.honeypot_field || $[17] !== formData.submit_button_text || $[18] !== formData.submit_target || $[19] !== formData.success_message || $[20] !== formData.turnstile_site_key || $[21] !== redirectUrl || $[22] !== renderField || $[23] !== submitted) {
    t8 = submitted && formData.success_message ? <p className={styles.success} role="status">{formData.success_message}</p> : <form action={`https://submit-form.com/${formData.form_id}`} method="POST" target={formData.submit_target || "_self"} className={styles.form}>{redirectUrl && <input type="hidden" name="_redirect" value={redirectUrl} />}{formData.fields.map((field_0, index_1) => {
        const fieldName_1 = field_0.field_name || "";
        return <div key={index_1} className={styles.fieldWrapper}>{field_0.field_type !== "checkbox" && <label htmlFor={fieldName_1} id={`${fieldName_1}-label`}>{field_0.field_label}</label>}{renderField(field_0)}</div>;
      })}{formData.turnstile_site_key && <div className="cf-turnstile" data-sitekey={formData.turnstile_site_key} />}{formData.honeypot_field && <input type="text" name="_gotcha" style={{
        display: "none"
      }} tabIndex={-1} autoComplete="off" />}<EffektButton variant={EffektButtonVariant.PRIMARY} type="submit">{formData.submit_button_text || "Submit"}</EffektButton></form>;
    $[14] = formData.fields;
    $[15] = formData.form_id;
    $[16] = formData.honeypot_field;
    $[17] = formData.submit_button_text;
    $[18] = formData.submit_target;
    $[19] = formData.success_message;
    $[20] = formData.turnstile_site_key;
    $[21] = redirectUrl;
    $[22] = renderField;
    $[23] = submitted;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t7 || $[26] !== t8) {
    t9 = <div className={styles.wrapper} id={t7}>{t8}</div>;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t6 || $[29] !== t9) {
    t10 = <>{t6}{t9}</>;
    $[28] = t6;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
function _temp(option_0, index_0) {
  return <option key={index_0} value={option_0.value}>{option_0.label}</option>;
}